import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth, SessionExpiredError } from "@/lib/auth-client";
import type {
  CreatePurchaseRequestRequest,
  PurchaseRequestDetailDto,
} from "@procureflow/shared-types";
import { PRForm, type PRFormInitialValues } from "@/components/pr-form";

// PR EDIT — a creator edits a Draft or ChangesRequested PR (header + line
// items) and re-saves via PUT /api/purchase-requests/:id. Reuses the shared
// PRForm (the same body the create route uses). Backend enforces the
// status + ownership gate; this route mirrors it so the user gets a friendly
// message instead of a raw 400/403.

export const Route = createFileRoute("/purchase-requests/$id/edit")({
  head: () => ({
    meta: [{ title: "Edit Purchase Request — ProcureFlow" }],
  }),
  component: PREditPage,
});

function PREditPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [detail, setDetail] = useState<PurchaseRequestDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchDetail() {
      // MSW mount-time race guard (CLAUDE.md MSW pattern).
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth(`/api/purchase-requests/${id}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) setError(body.message ?? "Failed to load purchase request.");
          return;
        }
        const { data } = await res.json();
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (err instanceof SessionExpiredError) return;
        if (!cancelled) setError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchDetail();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  const isCreator = user != null && detail != null && user.id === detail.requester.id;
  const isEditableStatus =
    detail != null && (detail.status === "Draft" || detail.status === "ChangesRequested");

  if (error || !detail || !isCreator || !isEditableStatus) {
    return (
      <div className="space-y-4">
        <Link
          to="/purchase-requests/$id"
          params={{ id }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to request
        </Link>
        <div
          data-testid="pr-edit-not-allowed"
          className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2"
        >
          <p className="text-sm font-medium text-foreground">This request can't be edited</p>
          <p className="text-sm text-muted-foreground">
            {error
              ? error
              : !isCreator
                ? "Only the request creator can edit it."
                : "Only Draft or Changes Requested purchase requests can be edited."}
          </p>
        </div>
      </div>
    );
  }

  const initialValues: PRFormInitialValues = {
    title: detail.title,
    cost_centre: detail.cost_centre,
    required_by_date: detail.required_by_date,
    justification: detail.justification,
    line_items: detail.line_items.map((li) => ({
      description: li.description,
      quantity: li.quantity,
      unit_of_measure: li.unit_of_measure,
      unit_price: li.unit_price,
      item_master_id: li.item_master_id ?? null,
    })),
  };

  async function handleUpdate(body: CreatePurchaseRequestRequest) {
    const res = await fetchWithAuth(`/api/purchase-requests/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.message ?? "Failed to save changes.");
    }
    navigate({ to: "/purchase-requests/$id", params: { id } });
  }

  return (
    <PRForm
      mode="edit"
      initialValues={initialValues}
      onSubmit={handleUpdate}
      onCancel={() => navigate({ to: "/purchase-requests/$id", params: { id } })}
    />
  );
}
